import type { Profile } from '@/lib/supabase';

/**
 * localStorage mirror of the signed-in profile (task.md §4.14 offline shell): when the app is reopened without a
 * connection the Supabase session is still in storage but the `profiles` read fails — the mirrored row lets the
 * shell render (role, name) until the network is back. A successful read always overwrites it; sign-out clears it.
 */
export const PROFILE_MIRROR_PREFIX = 'agrogruda_profile_';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function safeStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function saveProfileMirror(profile: Profile, storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.setItem(PROFILE_MIRROR_PREFIX + profile.id, JSON.stringify(profile));
  } catch {
    // storage full / blocked
  }
}

export function loadProfileMirror(
  userId: string,
  storage: StorageLike | null = safeStorage(),
): Profile | null {
  try {
    const raw = storage?.getItem(PROFILE_MIRROR_PREFIX + userId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<Profile>;
    if (candidate.id !== userId || typeof candidate.name !== 'string' || typeof candidate.role !== 'string') {
      return null;
    }
    return candidate as Profile;
  } catch {
    return null;
  }
}

export function clearProfileMirror(userId: string, storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(PROFILE_MIRROR_PREFIX + userId);
  } catch {
    // nothing to clear
  }
}
